import { Injectable } from '@angular/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Platform } from '@ionic/angular';
import { Auth } from '@angular/fire/auth';
import { DatabaseService } from './database.service';
import { Capacitor } from '@capacitor/core';
import { Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FcmProvider {
  private messageSubject = new Subject<any>();
  private listenersInitialized = false;
  private tokenListenerInitialized = false;
  private storedToken: string | null = null;
  private tokenResolvers: Array<(token: string) => void> = [];

  constructor(
    private dbService: DatabaseService,
    private firebaseAuth: Auth,
    private platform: Platform,
  ) {
    this.initializeListeners();
  }

  private async initializeTokenListener() {
    if (!Capacitor.isNativePlatform() || this.tokenListenerInitialized) {
      return;
    }

    try {
      // Listen for FCM token refresh
      await FirebaseMessaging.addListener('tokenReceived', (event) => {
        console.log('[FCM] FCM token received:', event.token.substring(0, 20) + '...');
        this.storedToken = event.token;
        // Resolve all pending token requests
        this.tokenResolvers.forEach(resolve => resolve(event.token));
        this.tokenResolvers = [];
      });
      
      this.tokenListenerInitialized = true;
    } catch (error) {
      console.error('[FCM] Error setting up token listeners:', error);
    }
  }

  private async initializeListeners() {
    if (!Capacitor.isNativePlatform() || this.listenersInitialized) {
      return;
    }

    try {
      // Listen for FCM notifications (foreground/background)
      await FirebaseMessaging.addListener('notificationReceived', (event) => {
        console.log('[FCM] FCM notification received:', event);
        const transformedMsg = this.transformFirebaseNotification(event, false);
        this.messageSubject.next(transformedMsg);
      });

      // Listen for notification tapped/opened
      await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        console.log('[FCM] FCM notification tapped:', event);
        const transformedMsg = this.transformFirebaseNotification(event.notification, true);
        this.messageSubject.next(transformedMsg);
      });

      // Also keep PushNotifications listeners for compatibility
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[FCM] Push notification received (foreground):', notification);
        const transformedMsg = this.transformNotification(notification, false);
        this.messageSubject.next(transformedMsg);
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('[FCM] Push notification tapped:', action);
        const transformedMsg = this.transformNotification(action.notification, true);
        this.messageSubject.next(transformedMsg);
      });

      this.listenersInitialized = true;
    } catch (error) {
      console.error('[FCM] Error initializing push notification listeners:', error);
    }
  }

  private transformNotification(notification: any, wasTapped: boolean): any {
    // Transform Capacitor push notification format to match old FirebaseX format
    const transformed: any = {
      tap: wasTapped,
      ...notification.data, // Include all custom data
    };

    // Add iOS format (aps.alert) for compatibility
    if (notification.title || notification.body) {
      transformed.aps = {
        alert: {
          title: notification.title || '',
          body: notification.body || '',
        }
      };
    }

    // Preserve notification properties
    if (notification.title) transformed.title = notification.title;
    if (notification.body) transformed.body = notification.body;
    if (notification.subtitle) transformed.subtitle = notification.subtitle;
    if (notification.id) transformed.id = notification.id;
    if (notification.link) transformed.link = notification.link;
    if (notification.clickAction) transformed.clickAction = notification.clickAction;

    return transformed;
  }

  private transformFirebaseNotification(event: any, wasTapped: boolean): any {
    // Transform Firebase Messaging notification format to match old FirebaseX format
    const notification = event.notification || event;
    const transformed: any = {
      tap: wasTapped,
      ...event.data, // Include all custom data
    };

    // Add iOS format (aps.alert) for compatibility
    if (notification.title || notification.body) {
      transformed.aps = {
        alert: {
          title: notification.title || '',
          body: notification.body || '',
        }
      };
    }

    // Preserve notification properties
    if (notification.title) transformed.title = notification.title;
    if (notification.body) transformed.body = notification.body;
    if (notification.data) {
      Object.assign(transformed, notification.data);
    }

    return transformed;
  }

  // Get FCM token using Firebase Messaging
  async getToken() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // If we already have a token, return it
    if (this.storedToken) {
      return this.storedToken;
    }

    try {
      // Ensure listeners are set up
      await this.initializeTokenListener();
      
      // Request permissions using Firebase Messaging
      const permissionResult = await FirebaseMessaging.requestPermissions();
      if (permissionResult.receive !== 'granted') {
        console.warn('[FCM] Push notification permissions not granted');
        return;
      }

      // Get FCM token directly
      console.log('[FCM] Requesting FCM token...');
      const result = await FirebaseMessaging.getToken();
      
      if (result.token) {
        console.log('[FCM] FCM token received:', result.token.substring(0, 20) + '...');
        this.storedToken = result.token;
        return result.token;
      } else {
        console.warn('[FCM] No token returned from Firebase Messaging');
        return null;
      }
    } catch (error) {
      console.error('[FCM] Error getting FCM token:', error);
      return null;
    }
  }

  async saveToken(): Promise<any> {
    const token = await this.getToken();
    if(token) {
      console.log('[FCM] Got token, saving to database...');
      return this.saveTokenToDatabase(token);
    } else {
      console.warn('[FCM] No token received from getToken()');
    }
  }

  async removeToken(uid): Promise<any> {
    const token = await this.getToken();
    if(token) {
      return this.removeTokenFromDatabase(uid, token);
    }
  }

  // Save the token to database
  private async saveTokenToDatabase(token) {
    const currentUser = this.firebaseAuth.currentUser;
    if (!token || !currentUser) {
      console.warn('[FCM] Cannot save token - missing token or user', { token: !!token, user: !!currentUser });
      return;
    }
    const uid = currentUser.uid;
    console.log('[FCM] Saving token to database', { uid, token: token.substring(0, 20) + '...' });
    try {
      await this.dbService.setRef(`/devices/${uid}/${token}`, true);
      console.log('[FCM] Token saved successfully to /devices/' + uid + '/' + token.substring(0, 20) + '...');
    } catch (error) {
      console.error('[FCM] Error saving token to database:', error);
      throw error;
    }
  }

  private removeTokenFromDatabase(uid, token) {
    if (!token || !uid) return;
    return this.dbService.removeRef(`/devices/${uid}/${token}`);
  }

  // Listen to incoming FCM messages - returns Observable for compatibility
  onMessageReceived(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  // Legacy method name - kept for compatibility
  listenToNotifications() {
    return this.onMessageReceived();
  }

}